import { MoreVertRounded } from "@mui/icons-material";
import { Box, Button, IconButton, Link, Menu, MenuItem, Stack, styled, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { Links } from "../../constants";
import { useSession, useSignalR } from "../../hooks";
import { MeetingNoteDto, useDeleteMeetingNote, useEditMeetingNote } from "../../http";
import { useDestructiveConfirm } from "../../utilities";
import { Avatar } from "../Avatar";
import { isWhitespace, TextEditor, TextViewer } from "../textEditor";

const EditRow = styled(Stack)<{ $isReadonly: boolean; $isHovering: boolean; $isEditing: boolean }>`
  background-color: ${({ $isReadonly, $isHovering, $isEditing, theme }) =>
    ($isHovering || $isEditing) && !$isReadonly ? theme.palette.primary.transparent5 : "transparent"};
  transition: ${({ theme }) => theme.transitions.create("background-color")};
`;

interface IProps {
  meetingGuid: string;
  meetingNote: MeetingNoteDto;
}

const MeetingNote = (props: IProps) => {
  const { userId } = useSession();
  const { mutate: editMeetingNote } = useEditMeetingNote();
  const { mutate: deleteMeetingNote } = useDeleteMeetingNote();
  const confirm = useDestructiveConfirm();
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [editedNoteText, setEditedNoteText] = useState("");
  const signalR = useSignalR();

  const isReadonly = props.meetingNote.createdByUser.userId !== userId;

  async function handleEditClicked() {
    setMenuAnchor(null);
    setIsEditing(true);
    setEditedNoteText(props.meetingNote.text);
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    const { confirmed } = await confirm({
      title: "Delete Meeting Note",
      content: "Are you sure you want to delete this note?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMeetingNote(
      {
        meetingGuid: props.meetingGuid,
        meetingNoteGuid: props.meetingNote.guid,
      },
      {
        onSuccess: () => signalR.sendRefetchMeetingNotes(props.meetingGuid),
      }
    );
  }

  function handleCancelEditClicked() {
    setIsEditing(false);
    setEditedNoteText("");
  }

  async function handleSaveEditClicked() {
    setIsEditing(false);
    setEditedNoteText("");

    editMeetingNote(
      {
        meetingGuid: props.meetingGuid,
        meetingNoteGuid: props.meetingNote.guid,
        text: editedNoteText,
      },
      {
        onSuccess: () => signalR.sendRefetchMeetingNotes(props.meetingGuid),
      }
    );
  }

  return (
    <EditRow
      $isReadonly={isReadonly}
      $isHovering={isHovering}
      $isEditing={isEditing}
      direction="row"
      spacing={1}
      sx={{
        px: { xs: 0.5, sm: 1 },
        py: isEditing ? 0.75 : 0.5,
        alignItems: "flex-start",
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Link href={Links.User(props.meetingNote.createdByUser.userId)} underline="none">
        <Avatar sx={{ mt: "4px" }} user={props.meetingNote.createdByUser} border />
      </Link>

      <Stack spacing={0.25} sx={{ minWidth: 0, width: "100%" }}>
        {!isEditing && (
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Stack
              direction="column"
              spacing={-0.25}
              sx={{
                minWidth: 0,
                alignItems: "baseline",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Link variant="body2" href={Links.User(props.meetingNote.createdByUser.userId)} underline="none">
                {props.meetingNote.createdByUser.displayName}
                {!props.meetingNote.createdByUser.enabled && " (deactivated)"}
              </Link>
              <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                {DateTime.fromISO(props.meetingNote.createdDateTimeUtc, { zone: "utc" })
                  .toLocal()
                  .toLocaleString(DateTime.DATETIME_FULL)}
              </Typography>
            </Stack>

            {!isReadonly && isHovering && (
              <>
                <IconButton
                  size="small"
                  sx={{ alignSelf: "center" }}
                  onClick={(e) => {
                    setMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertRounded />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => {
                    setMenuAnchor(null);
                    setIsHovering(false);
                  }}
                >
                  <MenuItem onClick={handleEditClicked}>Edit</MenuItem>
                  <MenuItem onClick={handleDeleteClicked} sx={{ color: "error.main" }}>
                    Delete
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        )}

        <Box>
          {isEditing ? (
            <TextEditor initialValue={editedNoteText} onChange={(value) => setEditedNoteText(value)} />
          ) : (
            <TextViewer value={props.meetingNote.text} />
          )}

          {!isEditing && props.meetingNote.createdDateTimeUtc !== props.meetingNote.modifiedDateTimeUtc && (
            <Typography variant="caption">Edited</Typography>
          )}
        </Box>

        {isEditing && (
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end", pt: 0.25 }}>
            <Button variant="text" onClick={handleCancelEditClicked}>
              Cancel
            </Button>
            <Button variant="tertiary" disabled={isWhitespace(editedNoteText)} onClick={handleSaveEditClicked}>
              Save
            </Button>
          </Stack>
        )}
      </Stack>
    </EditRow>
  );
};

export { MeetingNote };
